import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { ChevronLeft, Lock, Mic, Send, Trash2, X } from 'lucide-react';
import './style.css';

export interface AudioRecorderControlHandle {
  cancel: () => void;
}

interface AudioRecorderControlProps {
  onRecorded: (file: File) => void;
  onError: (message: string) => void;
  onRecordingChange?: (recording: boolean) => void;
  onActiveChange?: (active: boolean) => void;
  disabled?: boolean;
  audioLabel: string;
  cancelLabel: string;
  holdLabel: string;
  microphoneError: string;
  sendLabel: string;
}

type RecorderMode = 'idle' | 'ready' | 'recording' | 'locked';

const HOLD_DELAY_MS = 350;
const CANCEL_DISTANCE_PX = 72;
const LOCK_DISTANCE_PX = 58;

function formatRecordingTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

function getSupportedMimeType(): string | undefined {
  if (typeof MediaRecorder.isTypeSupported !== 'function') return undefined;

  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
  ];

  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate));
}

function getAudioExtension(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('ogg')) return 'ogg';
  return 'webm';
}

const AudioRecorderControl = forwardRef<AudioRecorderControlHandle, AudioRecorderControlProps>(({
  onRecorded,
  onError,
  onRecordingChange,
  onActiveChange,
  disabled = false,
  audioLabel,
  cancelLabel,
  holdLabel,
  microphoneError,
  sendLabel,
}, ref) => {
  const [mode, setMode] = useState<RecorderMode>('idle');
  const [recordingTime, setRecordingTime] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [pressing, setPressing] = useState(false);

  const modeRef = useRef<RecorderMode>('idle');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPositionRef = useRef<{ x: number; y: number } | null>(null);
  const gestureActiveRef = useRef(false);
  const startingRef = useRef(false);
  const mountedRef = useRef(true);
  const activeRef = useRef(false);

  const updateMode = useCallback((nextMode: RecorderMode) => {
    const active = nextMode !== 'idle';
    if (activeRef.current !== active) {
      activeRef.current = active;
      onActiveChange?.(active);
    }
    modeRef.current = nextMode;
    setMode(nextMode);
  }, [onActiveChange]);

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const resetGesture = useCallback(() => {
    gestureActiveRef.current = false;
    startPositionRef.current = null;
    setPressing(false);
    setDragX(0);
    setDragY(0);
  }, []);

  const finishRecording = useCallback((attach: boolean) => {
    clearTimers();
    resetGesture();
    setRecordingTime(0);
    onRecordingChange?.(false);

    const recorder = recorderRef.current;
    recorderRef.current = null;
    updateMode('idle');

    if (!recorder) {
      stopStream();
      chunksRef.current = [];
      return;
    }

    const finalize = () => {
      const mimeType = recorder.mimeType || chunksRef.current[0]?.type || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: mimeType });
      chunksRef.current = [];
      stopStream();

      if (attach && blob.size > 0) {
        const extension = getAudioExtension(mimeType);
        onRecorded(new File([blob], `post-audio-${Date.now()}.${extension}`, { type: mimeType }));
      }
    };

    recorder.onstop = finalize;
    recorder.onerror = () => {
      chunksRef.current = [];
      stopStream();
      if (mountedRef.current) onError(microphoneError);
    };

    if (recorder.state === 'inactive') finalize();
    else recorder.stop();
  }, [clearTimers, microphoneError, onError, onRecorded, onRecordingChange, resetGesture, stopStream, updateMode]);

  const cancel = useCallback(() => {
    startingRef.current = false;
    finishRecording(false);
  }, [finishRecording]);

  const startRecording = useCallback(async () => {
    if (startingRef.current || recorderRef.current || !gestureActiveRef.current) return;

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      resetGesture();
      updateMode('ready');
      onError(microphoneError);
      return;
    }

    startingRef.current = true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      startingRef.current = false;

      // The permission prompt can outlive the press. Never leave an unattended
      // recording running when the pointer was already released.
      if (!gestureActiveRef.current || !mountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      const mimeType = getSupportedMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.start();

      setRecordingTime(0);
      updateMode('recording');
      onRecordingChange?.(true);
      timerRef.current = setInterval(() => {
        setRecordingTime((current) => current + 1);
      }, 1000);
    } catch {
      startingRef.current = false;
      resetGesture();
      updateMode('ready');
      onError(microphoneError);
    }
  }, [microphoneError, onError, onRecordingChange, resetGesture, updateMode]);

  useImperativeHandle(ref, () => ({ cancel }), [cancel]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearTimers();
      const recorder = recorderRef.current;
      recorderRef.current = null;
      if (recorder && recorder.state !== 'inactive') {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.stop();
      }
      chunksRef.current = [];
      stopStream();
    };
  }, [clearTimers, stopStream]);

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (modeRef.current === 'locked') {
      finishRecording(true);
      return;
    }
    if (modeRef.current !== 'ready' || disabled) return;

    event.currentTarget.setPointerCapture?.(event.pointerId);
    gestureActiveRef.current = true;
    startPositionRef.current = { x: event.clientX, y: event.clientY };
    setPressing(true);
    setDragX(0);
    setDragY(0);

    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null;
      void startRecording();
    }, HOLD_DELAY_MS);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const startPosition = startPositionRef.current;
    if (!startPosition || modeRef.current === 'locked') return;

    const rawX = event.clientX - startPosition.x;
    const rawY = event.clientY - startPosition.y;
    const horizontalDistance = Math.abs(rawX);
    const upwardDistance = Math.max(0, -rawY);
    const movingUp = upwardDistance > 6 && upwardDistance >= horizontalDistance * 0.7;

    if (movingUp) {
      setDragX(0);
      setDragY(-Math.min(upwardDistance, 90));
    } else if (horizontalDistance > 6) {
      setDragX(Math.max(-90, Math.min(rawX, 90)));
      setDragY(0);
    }

    if (upwardDistance >= LOCK_DISTANCE_PX && movingUp && recorderRef.current) {
      gestureActiveRef.current = false;
      startPositionRef.current = null;
      setPressing(false);
      setDragX(0);
      setDragY(0);
      updateMode('locked');
      return;
    }

    if (horizontalDistance >= CANCEL_DISTANCE_PX && horizontalDistance > upwardDistance) {
      cancel();
    }
  };

  const handlePointerEnd = (cancelled: boolean) => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }

    const wasRecording = !!recorderRef.current;
    const locked = modeRef.current === 'locked';
    resetGesture();

    if (cancelled && wasRecording && !locked) {
      finishRecording(false);
    } else if (wasRecording && !locked) {
      finishRecording(true);
    }
  };

  if (mode === 'idle') {
    return (
      <button
        type="button"
        className="post-audio-open-button"
        aria-label={audioLabel}
        title={audioLabel}
        disabled={disabled}
        onClick={() => updateMode('ready')}
      >
        <Mic className="icon-add-emoji" />
      </button>
    );
  }

  const recording = mode === 'recording' || mode === 'locked';
  const locked = mode === 'locked';

  return (
    <div className={`post-audio-recorder ${recording ? 'is-recording' : 'is-ready'}`}>
      <div className="post-audio-recorder-left">
        {locked ? (
          <button type="button" className="post-audio-action post-audio-cancel" onClick={() => finishRecording(false)} aria-label={cancelLabel}>
            <Trash2 size={19} />
          </button>
        ) : recording ? (
          <div className="post-audio-cancel-hint" style={{ opacity: Math.max(0.4, Math.min(1, Math.abs(dragX) / CANCEL_DISTANCE_PX)) }}>
            <ChevronLeft size={14} />
            <span>{cancelLabel}</span>
          </div>
        ) : (
          <button type="button" className="post-audio-action post-audio-close" onClick={() => updateMode('idle')} aria-label={cancelLabel}>
            <X size={18} />
          </button>
        )}
      </div>

      <div className="post-audio-recorder-status" aria-live="polite">
        {recording ? (
          <>
            <span className="post-audio-dot" />
            <span className="post-audio-time">{formatRecordingTime(recordingTime)}</span>
            <span className="post-audio-wave" aria-hidden="true">
              {Array.from({ length: 12 }).map((_, index) => (
                <span key={index} style={{ animationDelay: `${index * 0.08}s` }} />
              ))}
            </span>
          </>
        ) : (
          <span className="post-audio-hold-label">{holdLabel}</span>
        )}
      </div>

      <div className="post-audio-mic-area">
        {pressing && recording && dragY < -8 && (
          <span className={`post-audio-lock ${dragY <= -LOCK_DISTANCE_PX ? 'is-ready' : ''}`}>
            <Lock size={15} />
          </span>
        )}
        <button
          type="button"
          className={`post-audio-record-button ${pressing ? 'is-pressing' : ''} ${locked ? 'is-locked' : ''}`}
          style={{ transform: locked ? undefined : `translate(${dragX}px, ${dragY}px) scale(${pressing ? 1.12 : 1})` }}
          aria-label={locked ? sendLabel : holdLabel}
          title={locked ? sendLabel : holdLabel}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={() => handlePointerEnd(false)}
          onPointerCancel={() => handlePointerEnd(true)}
          onContextMenu={(event) => event.preventDefault()}
        >
          {locked ? <Send size={19} /> : <Mic size={19} />}
        </button>
      </div>
    </div>
  );
});

AudioRecorderControl.displayName = 'AudioRecorderControl';

export default AudioRecorderControl;
